/**
 * chatBelow — Prest0n's transcript under the workstation's screen where the chat cannot dock (Justin, 2026-10-17:
 * "below where it can't sit beside").
 *
 * WHERE. On /desktop, on a phone or a window too narrow to dock the chat (820px and under, the width docking stops
 * at: MOBILE_VIEWPORT_QUERY), the open chat was the full-screen takeover and covered the live desktop. Now the
 * workstation's screen sits on top and the chat stands under it with its composer at the foot of the screen. A wide
 * screen keeps the docked rail. A screen too short for both (an iPhone SE, a 12 mini) keeps the takeover.
 *
 * WHO DECIDES WHAT. Two halves meet here, and neither reads the other's code:
 *   - the page (src/hooks/useDesktopSession.ts, under both of /desktop's pages) measures its frame and asks for a
 *     place: where the chat's top edge would be under the frame, the highest it may rise to (the frame's top), and
 *     whether the two fit the screen;
 *   - the chat engine answers (useChatBelowPane): on a phone, open or fading out, under a page that asks, it draws
 *     its own full-screen pane with that top. Nothing else about its modes changes: open on a phone is still full
 *     screen, and only a person's gestures set the device's mode. The page draws its frame at the height below
 *     only while the chat says it holds its place there.
 *
 * OVER THE PAGE (2026-10-21: "it should be consistent with other pages"). Where the chat stands under the page, its
 * header carries the desktop's own full-screen button: pressed, the chat takes the whole page, as an open phone chat
 * does on every other page, and "exit full screen" stands it under the page again. Over the page it keeps its place
 * here (still shown, so the page keeps its frame and the chat's hold down to the typing least, and the way back never
 * vanishes on a screen with only that much room); the page going leaves it open, as a full-screen chat on any page.
 * The choice is the engine's (useGlobalChat's isChatOverPage): it lasts until the chat closes, the next open stands
 * under the page again, and only this tab's memory keeps it through a reload.
 * A store and not an attribute on <html>: the request carries numbers the pane puts on itself, and a value changing
 * on <html> restyles the whole document. The pane holds both numbers itself (registered not inherited:
 * chatLayoutProperties.ts).
 *
 * THE NUMBERS, tuned on the browser gates (scripts/check-desktop-page.mjs lays the page out with the chat under it;
 * scripts/check-desktop-layout.mjs lays the workstation's page out in the screens this leaves it):
 *   - the screen is 16:9 of its width, never under CHAT_BELOW_SCREEN_MIN_PX tall: under a finger the workstation's
 *     page needs 274px for its toolbar, a window picker of four and a desktop a quarter of the screen (the frame's floor
 *     is that and its own foot, so the floor is the screen's, never the frame's);
 *   - the chat keeps at least CHAT_BELOW_MIN_PX at rest when it comes to stand there (the pane's header and composer
 *     are about 175px on a phone, so a few lines of the transcript show), or the takeover serves;
 *   - once there it keeps its place down to CHAT_BELOW_TYPING_MIN_PX, so a chip or a banner coming and going never
 *     flips it between the two (the frame's foot keeps one line on a phone, desktopCss.ts);
 *   - while someone types in the chat it rises so CHAT_BELOW_TYPING_MIN_PX stay above the keyboard, never higher than
 *     the frame's top; typing in the workstation (a co-drive, a password) moves nothing.
 * On a 390px iPhone in Safari's 664px window: the screen 350×280 under the app's header, the chat the 256px under it;
 * in a 700px desktop window the screen at 16:9.
 *
 * A leaf on the first download: it imports React and keyboardInset.ts, never the engine or Firebase (the page, the
 * boot and the engine read it; the chat-isolation suites render it).
 */
import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import { KEYBOARD_INSET_VAR } from './keyboardInset';

/** Where the pane's top edge stands under the page, in viewport pixels. Held by the pane itself. */
export const CHAT_BELOW_TOP_VAR = '--prest0-chat-below-top';
/** The highest the pane may rise while someone types in it (the frame's top). Held by the pane itself. */
export const CHAT_BELOW_FLOOR_VAR = '--prest0-chat-below-floor';

/** The chat's least height at rest when it comes to stand under the page: under it, the takeover serves instead. */
export const CHAT_BELOW_MIN_PX = 240;
/** The workstation's screen's least height above the chat (the frame adds its own foot). */
export const CHAT_BELOW_SCREEN_MIN_PX = 280;
/** The pane's least height above the keyboard while someone types in it, and the least a chat already there keeps. */
export const CHAT_BELOW_TYPING_MIN_PX = 220;

/** The page's request: where the chat would stand, and whether the two fit the screen. */
export interface ChatBelowRequest {
  /** The pane's top edge: the frame's bottom, in viewport pixels. */
  readonly top: number;
  /** The frame's top: the highest the pane may rise. */
  readonly floor: number;
  readonly fits: boolean;
}

/**
 * The frame's height while the chat stands under it (PURE): the screen at 16:9 of its width plus the frame's own
 * chrome, never taller than leaves the chat its least and never shorter than the screen's floor. `room` is the
 * screen's height from the frame's top down; `standing`, the chat already stands there (it keeps its place down to
 * the typing least). It fits when the room holds both floors.
 */
export function chatBelowFrame(a: { screenWidth: number; chrome: number; room: number; standing?: boolean }): { height: number; fits: boolean } {
  const least = CHAT_BELOW_SCREEN_MIN_PX + a.chrome;
  const most = Math.floor(a.room) - (a.standing ? CHAT_BELOW_TYPING_MIN_PX : CHAT_BELOW_MIN_PX);
  const natural = Math.round((a.screenWidth * 9) / 16) + a.chrome;
  return { height: Math.round(Math.max(least, Math.min(natural, most))), fits: most >= least };
}

/**
 * The pane's own rules (interpolated into ChatContainer in styles.ts, and read by the browser gate): its top under
 * the page; while someone types in it, risen so the typing room stays above the keyboard (iOS through the inset,
 * Android through the window shrinking), never above the frame's top.
 */
export const CHAT_BELOW_PANE_CSS =
  `&[data-chat-below]{top:var(${CHAT_BELOW_TOP_VAR});}`
  + `&[data-chat-below]:focus-within{top:max(var(${CHAT_BELOW_FLOOR_VAR}),min(var(${CHAT_BELOW_TOP_VAR}),`
  + `calc(100% - var(${KEYBOARD_INSET_VAR}, 0px) - ${CHAT_BELOW_TYPING_MIN_PX}px)));}`;

/* ── the store ────────────────────────────────────────────────────────── */

let request: ChatBelowRequest | null = null;
let shown = false;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => {
    try { l(); } catch { /* a broken listener must not break the publish loop */ }
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The page asks for its place (null: the page is gone). Nothing is told when nothing changed. */
export function requestChatBelow(next: ChatBelowRequest | null): void {
  const same = next === null
    ? request === null
    : request !== null && request.top === next.top && request.floor === next.floor && request.fits === next.fits;
  if (same) return;
  request = next === null ? null : { top: next.top, floor: next.floor, fits: next.fits };
  notify();
}

export function getChatBelowRequest(): ChatBelowRequest | null {
  return request;
}

/** React: the page's request, or null. */
export function useChatBelowRequest(): ChatBelowRequest | null {
  return useSyncExternalStore(subscribe, getChatBelowRequest, () => null);
}

/** The engine says whether the open chat holds its place under the page now (standing there, or over the page). */
export function setChatBelowShown(next: boolean): void {
  if (next === shown) return;
  shown = next;
  notify();
}

export function getChatBelowShown(): boolean {
  return shown;
}

/** React: does the open chat hold its place under the page now (standing there, or over the page)? */
export function useChatBelowShown(): boolean {
  return useSyncExternalStore(subscribe, getChatBelowShown, () => false);
}

/* ── the engine's half ────────────────────────────────────────────────── */

export interface ChatBelowPaneInput {
  /** A phone-width viewport, and the pane is the full-screen one (the only one that stands under a page). */
  phone: boolean;
  open: boolean;
  /** config.hidden: the chat is not drawn on this page. */
  hidden: boolean;
  /** The person took the chat over the whole page (the header's full-screen button): the takeover, its place held. */
  over: boolean;
  /** Puts the chat away when the page goes while the chat stands under it (the followed-link rule); null: never. */
  onPageLeft: (() => void) | null;
}

export interface ChatBelowPane {
  /** The pane stands under a page (open, or fading out where it stood). */
  placed: boolean;
  /** The two numbers the pane holds while placed. */
  style: CSSProperties;
  /** A page asks for the chat under it and the two fit: the header offers the whole page and the way back. */
  offered: boolean;
}

const NO_STYLE: CSSProperties = {};

/**
 * The engine's answer, here so the engine's own file pays for one call. Placed: on a phone, under a page whose
 * request fits. A minimized pane keeps its place while it fades, so it never flashes full screen on its way out, and
 * so does the pane when the page goes while it stands there: it is put away from where it stood (onPageLeft, once).
 * Rotating, widening or a request that no longer fits puts nothing away: the takeover (or the dock) serves.
 * Over the page the pane is drawn as the takeover while its place is held: still shown, never put away by the page
 * going, and drawn there again the moment the person brings it back (or fading out as the takeover, minimized).
 */
export function useChatBelowPane({ phone, open, hidden, over, onPageLeft }: ChatBelowPaneInput): ChatBelowPane {
  const asked = useChatBelowRequest();
  const memory = useRef<{ stood: ChatBelowRequest | null; shown: boolean; left: boolean }>({ stood: null, shown: false, left: false });
  const m = memory.current;
  const fits = phone && !hidden && asked !== null && asked.fits;
  // The page went while the chat stood under it, and the chat is about to step aside from there.
  const leaving = onPageLeft !== null && m.shown && asked === null && !m.left;
  if (fits) m.stood = asked;
  else if (!leaving && (open || !phone || hidden)) m.stood = null;
  const at = fits ? asked : m.stood;
  const isShown = at !== null && open;
  // Over the page: the takeover, with its place held for the way back.
  const drawn = over ? null : at;
  const pageLeft = useRef(onPageLeft);
  pageLeft.current = onPageLeft;
  useLayoutEffect(() => {
    m.shown = isShown && !over;
    if (isShown && asked !== null) m.left = false;
    setChatBelowShown(isShown);
    if (leaving) {
      m.left = true;
      pageLeft.current?.();
    }
  });
  useLayoutEffect(() => () => setChatBelowShown(false), []);
  return {
    placed: drawn !== null,
    style: drawn === null
      ? NO_STYLE
      : ({ [CHAT_BELOW_TOP_VAR]: `${drawn.top}px`, [CHAT_BELOW_FLOOR_VAR]: `${drawn.floor}px` } as CSSProperties),
    offered: fits,
  };
}

/** Tests only: forget the store. */
export function resetChatBelowForTests(): void {
  request = null;
  shown = false;
  listeners.clear();
}
